<template>
  <div class="tp-view">
    <div v-if="store.loading && !store.overview" class="tp-state"><span class="mdi mdi-loading tp-state__spinner"></span></div>

    <div v-else-if="store.error" class="tp-state tp-state--message">
      <span class="mdi mdi-alert-circle-outline tp-state__icon"></span>
      <p>{{ store.error === 'PORTAL_DISABLED' ? 'El portal de inquilinos no está disponible por el momento.' : 'No se pudo cargar tu información.' }}</p>
    </div>

    <div v-else-if="store.overview && !store.overview.contracts.length" class="tp-state tp-state--message">
      <span class="mdi mdi-file-document-outline tp-state__icon"></span>
      <p>Tu cuenta todavía no tiene un contrato con acceso al portal.</p>
    </div>

    <template v-else-if="store.overview">
      <!-- Resumen financiero -->
      <section class="tp-summary">
        <div class="tp-summary__cell">
          <span class="tp-summary__label">Cargos del mes</span>
          <span class="tp-summary__value" style="color: #1e3a5f">{{ summaryText('total') }}</span>
        </div>
        <div class="tp-summary__cell">
          <span class="tp-summary__label">Pagado</span>
          <span class="tp-summary__value" :style="{ color: paidColor }">{{ summaryText('paid') }}</span>
        </div>
        <div class="tp-summary__cell">
          <span class="tp-summary__label">Saldo pendiente</span>
          <span class="tp-summary__value" :style="{ color: pendingColor }">{{ summaryText('pending') }}</span>
        </div>
      </section>

      <div class="tp-scroll">
        <div class="tp-column">
          <p class="tp-context" :style="{ color: contextColor }">{{ contextText }}</p>
          <p v-if="otherCurrencyText" class="tp-note">{{ otherCurrencyText }}</p>

          <div class="tp-controls">
            <TenantContractSelect
              :contracts="store.overview.contracts"
              :model-value="store.selectedContractId"
              @update:model-value="selectContract"
            />
            <div v-if="data && data.availablePeriods.length > 1" class="tp-period">
              <label class="tp-period__label" for="tp-period">Periodo</label>
              <select id="tp-period" class="tp-period__select" :value="periodKey" @change="onPeriod($event.target.value)">
                <option v-for="p in data.availablePeriods" :key="`${p.year}-${p.month}`" :value="`${p.year}-${p.month}`">
                  {{ periodLabel(p.year, p.month) }}
                </option>
              </select>
            </div>
            <span v-else-if="data" class="tp-period__single">{{ periodLabel(data.period.year, data.period.month) }}</span>
          </div>

          <div v-if="loading" class="tp-state"><span class="mdi mdi-loading tp-state__spinner"></span></div>
          <div v-else-if="loadError" class="tp-state tp-state--message">
            <p>No se pudieron cargar tus pagos.</p>
            <button class="tp-btn" @click="loadPayments">Reintentar</button>
          </div>

          <template v-else-if="data">
            <p v-if="data.priorBalance > 0" class="tp-prior">
              Tienes saldo de periodos anteriores por <strong class="tp-mono">{{ money(data.priorBalance, 'MXN') }}</strong>.
            </p>

            <section v-if="!data.groups.length" class="tp-empty">No hay cargos en este periodo.</section>

            <section v-else class="tp-table">
              <div class="tp-table__head">
                <span class="tp-col tp-col--concept">Concepto</span>
                <span class="tp-col tp-col--type">Tipo</span>
                <span class="tp-col tp-col--amount">Monto</span>
                <span class="tp-col tp-col--status">Estatus</span>
              </div>
              <template v-for="g in data.groups" :key="g.key">
                <div class="tp-group">{{ g.label }}</div>
                <div v-for="item in g.items" :key="item.id" class="tp-row">
                  <span class="tp-col tp-col--concept tp-row__concept">{{ item.concept }}</span>
                  <span class="tp-col tp-col--type"><span class="tp-tag" :class="`tp-tag--${g.key}`">{{ TYPE_LABEL[g.key] }}</span></span>
                  <span class="tp-col tp-col--amount tp-mono">{{ money(item.amount, item.currency) }}</span>
                  <span class="tp-col tp-col--status"><span class="tp-pill" :class="`tp-pill--${item.status.toLowerCase()}`">{{ STATUS_LABEL[item.status] }}</span></span>
                </div>
              </template>
              <div v-for="t in data.totals" :key="t.currency" class="tp-total">
                <span class="tp-total__label">Total del periodo{{ data.totals.length > 1 ? ` (${t.currency})` : '' }}</span>
                <span class="tp-total__value">{{ money(t.total, t.currency) }}</span>
              </div>
            </section>

            <!-- Estados de cuenta -->
            <section v-if="data.statementsEnabled">
              <h3 class="tp-label">Estados de cuenta</h3>
              <div v-if="statementsLoading" class="tp-state"><span class="mdi mdi-loading tp-state__spinner"></span></div>
              <div v-else-if="!statements.length" class="tp-empty">El parque aún no ha generado estados de cuenta</div>
              <div v-else class="tp-statements">
                <div v-for="s in statements" :key="s.id" class="tp-statement">
                  <div class="tp-statement__info">
                    <span class="tp-statement__period">{{ periodLabel(s.periodYear, s.periodMonth) }}</span>
                    <span class="tp-statement__date">Generado el {{ formatDate(s.generatedAt) }}</span>
                  </div>
                  <span class="tp-statement__total">{{ money(s.total, 'MXN') }}</span>
                  <a class="tp-statement__download" :href="statementDownloadUrl(s.id)" target="_blank" rel="noopener" :aria-label="`Descargar estado de cuenta de ${periodLabel(s.periodYear, s.periodMonth)}`">
                    <span class="mdi mdi-file-pdf-box"></span>
                  </a>
                </div>
              </div>
            </section>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { getPortalPayments, getPortalStatements, statementDownloadUrl } from '@/services/tenantPortal'
import { useTenantPortalStore } from '@/services/tenantPortalStore'
import TenantContractSelect from '@/components/TenantPortal/TenantContractSelect.vue'

const { state: store, loadOverview, selectContract } = useTenantPortalStore()

const data = ref(null)
const loading = ref(false)
const loadError = ref(false)
const statements = ref([])
const statementsLoading = ref(false)
const selectedPeriod = ref(null) // { year, month } o null = mes en curso

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const TYPE_LABEL = { rent: 'Renta', services: 'Servicios', additional: 'Adicional' }
const STATUS_LABEL = { PAID: 'Pagado', PARTIAL: 'Parcial', PENDING: 'Pendiente', OVERDUE: 'Vencido' }

const periodLabel = (y, m) => `${MONTHS[m - 1]} ${y}`
const periodKey = computed(() => (data.value ? `${data.value.period.year}-${data.value.period.month}` : ''))
function formatDate(iso) {
  const d = new Date(iso)
  return `${d.getDate()} ${SHORT[d.getMonth()]} ${d.getFullYear()}`
}
function money(amount, currency) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(amount || 0)
}

// --- Resumen ---
const primary = computed(() => data.value?.summaries[0] || { currency: 'MXN', total: 0, paid: 0, pending: 0 })
function summaryText(key) {
  return money(primary.value[key], primary.value.currency)
}
const GRAY = '#94a3b8'
const paidColor = computed(() => {
  const s = primary.value
  if (s.total > 0 && s.pending <= 0.005) return '#10b981'
  return s.paid > 0 ? '#f59e0b' : GRAY
})
const pendingColor = computed(() => {
  if (primary.value.pending <= 0.005) return GRAY
  return data.value?.context.kind === 'OVERDUE' ? '#ef4444' : '#f59e0b'
})
const contextText = computed(() => {
  const c = data.value?.context
  if (!c) return ''
  if (c.kind === 'OVERDUE') return `Vencido hace ${c.days} ${c.days === 1 ? 'día' : 'días'}`
  if (c.kind === 'DUE') return `Vence el ${formatDate(c.date)}`
  return 'Al corriente · Sin saldo pendiente'
})
const contextColor = computed(() => {
  const k = data.value?.context.kind
  return k === 'OVERDUE' ? '#ef4444' : k === 'DUE' ? '#f59e0b' : '#10b981'
})
const otherCurrencyText = computed(() => {
  const others = (data.value?.summaries || []).slice(1).filter((s) => s.pending > 0.005)
  if (!others.length) return ''
  return `Además tienes pendiente: ${others.map((s) => money(s.pending, s.currency)).join(' · ')}`
})

// --- Carga ---
async function loadPayments() {
  if (!store.selectedContractId) return
  loading.value = true
  loadError.value = false
  try {
    const p = selectedPeriod.value
    data.value = await getPortalPayments(store.selectedContractId, p?.year, p?.month)
  } catch {
    data.value = null
    loadError.value = true
  } finally {
    loading.value = false
  }
}

async function loadStatements() {
  statements.value = []
  if (!store.selectedContractId || !data.value?.statementsEnabled) return
  statementsLoading.value = true
  try {
    statements.value = await getPortalStatements(store.selectedContractId)
  } catch {
    statements.value = []
  } finally {
    statementsLoading.value = false
  }
}

function onPeriod(key) {
  const [year, month] = key.split('-').map(Number)
  selectedPeriod.value = { year, month }
  loadPayments()
}

async function loadAll() {
  await loadPayments()
  await loadStatements()
}

onMounted(async () => {
  try {
    await loadOverview()
  } catch {
    return
  }
  await loadAll()
})

watch(() => store.selectedContractId, (next, prev) => {
  if (prev !== null && next !== prev) {
    selectedPeriod.value = null
    loadAll()
  }
})
</script>

<style scoped>
.tp-view { flex: 1; min-height: 0; display: flex; flex-direction: column; background: #fff; }
.tp-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 40px 16px; color: #94a3b8; flex: 1; }
.tp-state--message { text-align: center; font: 400 13px Inter, sans-serif; }
.tp-state__icon { font-size: 32px; }
.tp-state__spinner { font-size: 28px; animation: tp-spin 1s linear infinite; }
@keyframes tp-spin { to { transform: rotate(360deg); } }
.tp-btn { height: 32px; padding: 0 14px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font: 500 13px Inter, sans-serif; cursor: pointer; }

.tp-summary { display: flex; align-items: stretch; min-height: 80px; flex-shrink: 0; background: #f0f4f8; border-bottom: 1px solid #e2e8f0; }
.tp-summary__cell { flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 4px; padding: 10px 8px; border-left: 1px solid #e2e8f0; min-width: 0; }
.tp-summary__cell:first-child { border-left: none; }
.tp-summary__label { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.tp-summary__value { font: 700 20px var(--font-mono); white-space: nowrap; }

.tp-scroll { flex: 1; overflow-y: auto; padding: 16px 16px 32px; }
.tp-column { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
.tp-context { margin: 0; text-align: center; font: 400 12px Inter, sans-serif; }
.tp-note { margin: -8px 0 0; text-align: center; font: 400 11px Inter, sans-serif; color: #94a3b8; }
.tp-prior { margin: 0; padding: 8px 12px; border-radius: 6px; background: #fffbeb; border: 1px solid #fde68a; font: 400 12px Inter, sans-serif; color: #92400e; }
.tp-mono { font-family: var(--font-mono); }

.tp-controls { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.tp-period { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.tp-period__label { font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; }
.tp-period__select { height: 32px; padding: 0 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; font: 500 13px Inter, sans-serif; color: #1e3a5f; }
.tp-period__single { margin-left: auto; font: 500 13px Inter, sans-serif; color: #1e3a5f; }

.tp-label { margin: 0 0 8px; font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.tp-empty { text-align: center; padding: 20px; font: 400 12px Inter, sans-serif; color: #94a3b8; }

.tp-table { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.tp-table__head, .tp-row { display: flex; align-items: center; gap: 8px; padding: 0 14px; }
.tp-table__head { height: 32px; background: #f8fafc; font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; }
.tp-row { min-height: 44px; border-top: 1px solid #f1f5f9; padding-top: 6px; padding-bottom: 6px; }
.tp-col--concept { width: 45%; min-width: 0; }
.tp-col--type { width: 14%; }
.tp-col--amount { flex: 1; text-align: right; font: 400 13px var(--font-mono); color: #1e3a5f; }
.tp-col--status { width: 22%; text-align: right; }
.tp-table__head .tp-col--amount { font: 500 11px Inter, sans-serif; color: #94a3b8; }
.tp-row__concept { font: 400 13px Inter, sans-serif; color: #1e3a5f; }
.tp-group { padding: 8px 14px 6px; border-top: 1px solid #e2e8f0; font: 500 11px Inter, sans-serif; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; background: #fcfdfe; }

.tp-tag { font: 500 10px Inter, sans-serif; padding: 2px 8px; border-radius: 4px; white-space: nowrap; }
.tp-tag--rent { background: #dbeafe; color: #1d4ed8; }
.tp-tag--services { background: #dcfce7; color: #15803d; }
.tp-tag--additional { background: #fef3c7; color: #b45309; }
.tp-pill { display: inline-block; font: 500 11px Inter, sans-serif; padding: 3px 10px; border-radius: 12px; white-space: nowrap; }
.tp-pill--paid { background: #dcfce7; color: #15803d; }
.tp-pill--pending, .tp-pill--partial { background: #fef3c7; color: #b45309; }
.tp-pill--overdue { background: #fee2e2; color: #b91c1c; }

.tp-total { display: flex; justify-content: space-between; align-items: center; padding: 12px 14px; border-top: 1px solid #e2e8f0; background: #f8fafc; }
.tp-total__label { font: 700 13px Inter, sans-serif; color: #1e3a5f; }
.tp-total__value { font: 700 13px var(--font-mono); color: #1e3a5f; }

.tp-statements { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.tp-statement { display: flex; align-items: center; gap: 12px; height: 48px; padding: 0 12px; border-top: 1px solid #f1f5f9; }
.tp-statement:first-child { border-top: none; }
.tp-statement__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.tp-statement__period { font: 500 13px Inter, sans-serif; color: #1e3a5f; }
.tp-statement__date { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.tp-statement__total { font: 400 12px var(--font-mono); color: #1e3a5f; }
.tp-statement__download { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; color: #ef4444; font-size: 20px; text-decoration: none; }
.tp-statement__download:hover { background: #fef2f2; }

@media (max-width: 768px) {
  .tp-summary__value { font-size: 15px; }
  .tp-scroll { padding: 12px 12px 24px; }
  .tp-table__head { display: none; }
  .tp-row { flex-wrap: wrap; row-gap: 4px; }
  .tp-col--concept { width: auto; flex: 1; }
  .tp-col--amount { flex: 0 0 auto; }
  .tp-col--type { width: auto; order: 3; }
  .tp-col--status { width: auto; order: 4; margin-left: auto; }
}
</style>